import { useState } from "react";
import {
  Search,
  Plus,
  ShieldCheck,
  ClipboardCheck,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  X,
  Trash2,
  Calendar,
  Percent,
} from "lucide-react";

const initialInspections = [
  {
    id: "QC-4001",
    item: "CNC Machined Bracket — Batch 22",
    category: "Dimensional",
    severity: "Minor",
    result: "Pass",
    inspector: "Sowmya Chandra",
    centre: "APIC Visakhapatnam",
    date: "2026-07-22",
    score: 96,
    notes: "All measurements within tolerance ±0.05mm.",
  },
  {
    id: "QC-4002",
    item: "Drone Frame Weld Joints",
    category: "Structural",
    severity: "Major",
    result: "Rework",
    inspector: "Karthik Iyer",
    centre: "APIC Vijayawada",
    date: "2026-07-25",
    score: 68,
    notes: "Two weld joints show porosity, needs re-weld before dispatch.",
  },
  {
    id: "QC-4003",
    item: "Resin Print — Enclosure Shells (x40)",
    category: "Surface Finish",
    severity: "Critical",
    result: "Fail",
    inspector: "Deepika Rao",
    centre: "APIC Tirupati",
    date: "2026-07-20",
    score: 41,
    notes: "Layer delamination on 12 of 40 units, batch quarantined.",
  },
  {
    id: "QC-4004",
    item: "Robotic Gripper Assembly",
    category: "Functional",
    severity: "Minor",
    result: "Pass",
    inspector: "Farhan Sheikh",
    centre: "APIC Kurnool",
    date: "2026-07-18",
    score: 92,
    notes: "Grip force and cycle test passed on first attempt.",
  },
  {
    id: "QC-4005",
    item: "PCB Reflow Batch — Sensor Boards",
    category: "Electrical",
    severity: "Major",
    result: "Pending",
    inspector: "Anita Reddy",
    centre: "APIC Tirupati",
    date: "2026-07-27",
    score: null,
    notes: "Awaiting continuity test results from lab.",
  },
  {
    id: "QC-4006",
    item: "Compressor Housing Casting",
    category: "Dimensional",
    severity: "Minor",
    result: "Pass",
    inspector: "Lakshmi Devi",
    centre: "APIC Kurnool",
    date: "2026-07-24",
    score: 98,
    notes: "Routine batch sampling, no deviations found.",
  },
];

const CATEGORIES = ["Dimensional", "Structural", "Surface Finish", "Functional", "Electrical"];
const SEVERITIES = ["Minor", "Major", "Critical"];
const RESULT_FILTERS = ["All", "Pass", "Pending", "Rework", "Fail"];
const RESULT_OPTIONS = ["Pass", "Pending", "Rework", "Fail"];

const RESULT_STYLES = {
  Pass: "bg-green-100 text-green-700",
  Pending: "bg-sky-100 text-sky-700",
  Rework: "bg-amber-100 text-amber-700",
  Fail: "bg-red-100 text-red-700",
};

const RESULT_ICONS = {
  Pass: <CheckCircle2 size={16} />,
  Pending: <Clock size={16} />,
  Rework: <AlertTriangle size={16} />,
  Fail: <XCircle size={16} />,
};

const SEVERITY_STYLES = {
  Minor: "bg-slate-100 text-slate-600",
  Major: "bg-orange-100 text-orange-700",
  Critical: "bg-red-100 text-red-700",
};

function StatCard({ icon, label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${tone}`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-extrabold text-slate-900 leading-tight">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children, small }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-0 sm:px-4">
      <div
        className={`bg-white w-full ${
          small ? "sm:max-w-xs" : "sm:max-w-md"
        } sm:rounded-2xl rounded-t-2xl p-6 max-h-[90vh] overflow-y-auto`}
      >
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-extrabold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function formatDate(d) {
  const dt = new Date(d);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function QualityScreen() {
  const [inspections, setInspections] = useState(initialInspections);
  const [search, setSearch] = useState("");
  const [activeResult, setActiveResult] = useState("All");
  const [showAddModal, setShowAddModal] = useState(false);
  const [resultTarget, setResultTarget] = useState(null); // id of inspection whose result popup is open

  const [newInsp, setNewInsp] = useState({
    item: "",
    category: CATEGORIES[0],
    severity: SEVERITIES[0],
    inspector: "",
    centre: "",
    date: "",
    notes: "",
  });

  const term = search.trim().toLowerCase();

  const filtered = inspections.filter((r) => {
    const matchesResult = activeResult === "All" || r.result === activeResult;
    const matchesSearch =
      !term ||
      r.item.toLowerCase().includes(term) ||
      r.id.toLowerCase().includes(term) ||
      r.inspector.toLowerCase().includes(term) ||
      r.centre.toLowerCase().includes(term);
    return matchesResult && matchesSearch;
  });

  const total = inspections.length;
  const passed = inspections.filter((r) => r.result === "Pass").length;
  const failed = inspections.filter((r) => r.result === "Fail").length;
  const pending = inspections.filter((r) => r.result === "Pending").length;

  const scored = inspections.filter((r) => typeof r.score === "number");
  const passRate = total ? Math.round((passed / total) * 100) : 0;

  const targetInspection = inspections.find((r) => r.id === resultTarget) || null;

  function handleAddInspection(e) {
    e.preventDefault();
    if (!newInsp.item || !newInsp.inspector || !newInsp.centre || !newInsp.date) return;
    const id = "QC-" + (4000 + inspections.length + 1);
    setInspections([
      ...inspections,
      {
        id,
        item: newInsp.item,
        category: newInsp.category,
        severity: newInsp.severity,
        result: "Pending",
        inspector: newInsp.inspector,
        centre: newInsp.centre,
        date: newInsp.date,
        score: null,
        notes: newInsp.notes,
      },
    ]);
    setNewInsp({ item: "", category: CATEGORIES[0], severity: SEVERITIES[0], inspector: "", centre: "", date: "", notes: "" });
    setShowAddModal(false);
  }

  function handleDelete(id) {
    setInspections((prev) => prev.filter((r) => r.id !== id));
  }

  function applyResult(id, result) {
    setInspections((prev) => prev.map((r) => (r.id === id ? { ...r, result } : r)));
    setResultTarget(null);
  }

  return (
    <div className="">
      <div className="max-w-6xl mx-auto" style={{ height: "90vh", overflow: "auto", scrollbarWidth: "none" }}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-1">
              Quality Control
            </h1>
            <p className="text-slate-500">{total} inspections across 4 APIC centres</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center gap-2 bg-gradient-to-br from-emerald-500 to-teal-500 text-white font-semibold px-5 py-3 rounded-xl shadow-sm hover:opacity-95 whitespace-nowrap"
          >
            <Plus size={18} />
            New Inspection
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<ClipboardCheck size={20} className="text-emerald-600" />}
            label="Total Inspections"
            value={total}
            tone="bg-emerald-50"
          />
          <StatCard
            icon={<Percent size={20} className="text-teal-600" />}
            label="Pass Rate"
            value={`${passRate}%`}
            tone="bg-teal-50"
          />
          <StatCard
            icon={<Clock size={20} className="text-sky-600" />}
            label="Pending"
            value={pending}
            tone="bg-sky-50"
          />
          <StatCard
            icon={<XCircle size={20} className="text-red-600" />}
            label="Failed"
            value={failed}
            tone="bg-red-50"
          />
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 mb-5 shadow-sm">
          <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-4 py-3 mb-4">
            <Search size={18} className="text-slate-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search item, ID, inspector, or centre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 outline-none text-sm text-slate-900 placeholder:text-slate-400"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {RESULT_FILTERS.map((s) => {
              const count = s === "All" ? inspections.length : inspections.filter((r) => r.result === s).length;
              const active = activeResult === s;
              return (
                <button
                  key={s}
                  onClick={() => setActiveResult(s)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    active
                      ? "bg-gradient-to-br from-emerald-500 to-teal-500 text-white border-transparent"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {s} <span className="opacity-75 font-bold text-xs">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <p className="text-sm text-slate-500 mb-4 px-1">
          Showing {filtered.length} of {inspections.length} inspections
        </p>

        {/* Desktop table */}
        <div className="hidden md:block bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-slate-500">
                <th className="px-5 py-3 font-semibold">Ticket ID</th>
                <th className="px-5 py-3 font-semibold">Item</th>
                <th className="px-5 py-3 font-semibold">Category</th>
                <th className="px-5 py-3 font-semibold">Severity</th>
                <th className="px-5 py-3 font-semibold">Inspector</th>
                <th className="px-5 py-3 font-semibold">Date</th>
                <th className="px-5 py-3 font-semibold">Score</th>
                <th className="px-5 py-3 font-semibold">Result</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-14 text-slate-500">
                    No inspections found. Try a different search or filter.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-4 font-mono text-xs text-slate-600">{r.id}</td>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-slate-900">{r.item}</p>
                      <p className="text-xs text-slate-500">{r.centre}</p>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{r.category}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${SEVERITY_STYLES[r.severity]}`}>
                        {r.severity}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{r.inspector}</td>
                    <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{formatDate(r.date)}</td>
                    <td className="px-5 py-4 text-slate-600">
                      {typeof r.score === "number" ? `${r.score}%` : "—"}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${RESULT_STYLES[r.result]}`}>
                        {r.result}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setResultTarget(r.id)}
                          className="flex items-center gap-1 text-xs font-semibold text-emerald-600 border border-emerald-200 rounded-lg px-3 py-1.5 hover:bg-emerald-50 whitespace-nowrap"
                        >
                          <ShieldCheck size={13} />
                          Update
                        </button>
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="text-slate-400 hover:text-red-600 p-1.5"
                          title="Delete inspection"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden flex flex-col gap-4">
          {filtered.length === 0 ? (
            <div className="text-center py-14 px-5 bg-white border border-dashed border-slate-200 rounded-2xl text-slate-500">
              No inspections found. Try a different search or filter.
            </div>
          ) : (
            filtered.map((r) => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <span className="font-mono text-xs border border-slate-200 rounded-full px-3 py-1 text-slate-600">
                    {r.id}
                  </span>
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${RESULT_STYLES[r.result]}`}>
                    {r.result}
                  </span>
                </div>
                <p className="text-lg font-extrabold text-slate-900">{r.item}</p>
                <p className="text-sm text-slate-500 mb-3">
                  {r.category} · {r.centre}
                </p>

                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-slate-500">Severity</span>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${SEVERITY_STYLES[r.severity]}`}>
                    {r.severity}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-slate-500">Inspector</span>
                  <span className="font-semibold text-slate-900">{r.inspector}</span>
                </div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-slate-500 flex items-center gap-1">
                    <Calendar size={13} /> Date
                  </span>
                  <span className="font-semibold text-slate-900">{formatDate(r.date)}</span>
                </div>
                <div className="flex items-center justify-between text-sm mb-4">
                  <span className="text-slate-500">Score</span>
                  <span className="font-semibold text-slate-900">
                    {typeof r.score === "number" ? `${r.score}%` : "—"}
                  </span>
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                  <button
                    onClick={() => setResultTarget(r.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold text-emerald-600 border border-emerald-200 rounded-lg px-3 py-2 hover:bg-emerald-50"
                  >
                    <ShieldCheck size={14} />
                    Update Result
                  </button>
                  <button
                    onClick={() => handleDelete(r.id)}
                    className="text-slate-400 hover:text-red-600 p-2 border border-slate-200 rounded-lg"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Inspection Modal */}
      {showAddModal && (
        <Modal title="New Quality Inspection" onClose={() => setShowAddModal(false)}>
          <form onSubmit={handleAddInspection} className="flex flex-col gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Item / Batch</label>
              <input
                type="text"
                value={newInsp.item}
                onChange={(e) => setNewInsp({ ...newInsp, item: e.target.value })}
                placeholder="e.g. CNC Machined Bracket — Batch 23"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Category</label>
                <select
                  value={newInsp.category}
                  onChange={(e) => setNewInsp({ ...newInsp, category: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Severity</label>
                <select
                  value={newInsp.severity}
                  onChange={(e) => setNewInsp({ ...newInsp, severity: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400"
                >
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Inspector</label>
              <input
                type="text"
                value={newInsp.inspector}
                onChange={(e) => setNewInsp({ ...newInsp, inspector: e.target.value })}
                placeholder="e.g. Sowmya Chandra"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">APIC Centre</label>
                <input
                  type="text"
                  value={newInsp.centre}
                  onChange={(e) => setNewInsp({ ...newInsp, centre: e.target.value })}
                  placeholder="e.g. APIC Kurnool"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Inspection Date</label>
                <input
                  type="date"
                  value={newInsp.date}
                  onChange={(e) => setNewInsp({ ...newInsp, date: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Notes</label>
              <textarea
                value={newInsp.notes}
                onChange={(e) => setNewInsp({ ...newInsp, notes: e.target.value })}
                placeholder="Describe findings or checks performed..."
                rows={3}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400 resize-none"
              />
            </div>

            <div className="flex gap-3 mt-2">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="flex-1 border border-slate-200 rounded-lg py-2.5 font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-gradient-to-br from-emerald-500 to-teal-500 text-white rounded-lg py-2.5 font-semibold hover:opacity-95"
              >
                Create Inspection
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Update Result Popup */}
      {resultTarget && targetInspection && (
        <Modal title="Update Result" onClose={() => setResultTarget(null)} small>
          <p className="text-sm text-slate-500 mb-4">
            <span className="font-semibold text-slate-900">{targetInspection.item}</span>
            <br />
            <span className="font-mono text-xs">{targetInspection.id}</span>
          </p>
          <div className="flex flex-col gap-2">
            {RESULT_OPTIONS.map((s) => {
              const isCurrent = targetInspection.result === s;
              return (
                <button
                  key={s}
                  onClick={() => applyResult(targetInspection.id, s)}
                  className={`flex items-center justify-between gap-2 px-4 py-3 rounded-xl border text-sm font-semibold transition-colors ${
                    isCurrent
                      ? `${RESULT_STYLES[s]} border-transparent`
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {RESULT_ICONS[s]}
                    {s}
                  </span>
                  {isCurrent && <CheckCircle2 size={16} />}
                </button>
              );
            })}
          </div>
        </Modal>
      )}
    </div>
  );
}